import React, { FC, useCallback, useState, useRef, useEffect } from "react";
import { CategoryProps, Chat } from "./chat";
import { Message } from "../../components/chat/chat";
import {
  getIAResposta,
  getConversationId,
  getIAAudioReponse,
  CONVERSATION_STORAGE_KEY,
} from "../../apis/ia";
import { cancelarVoz } from "../../util/voice";
import { MESSAGE_TYPES } from "./constants";
import {
  createLisMessage,
  createErrorMessage,
  createLisAudioMessage,
} from "./utils";
import { usePortalProvider } from "../../providers/portal-provider";
import { useAvatarLis } from "../../hooks/use-avatar-lis";
import { AiAgentConversationProvider } from "../../contexts/ai-agent-conversation-context";

export const ChatContainer: FC = () => {
  const conversationIdRef = useRef<string | null | undefined>(null);
  const validConversationIdRef = useRef<boolean>(false);
  const [conversationId, setConversationId] = useState<string | null>(null);

  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const { accessToken, profile } = usePortalProvider();
  const userInfoInjected = useRef(false);
  const avatarLis = useAvatarLis();

  const now = new Date();
  const time = now.toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  });
  const [messages, setMessages] = useState<Message[]>([
    {
      from: "lis",
      text: "Olá, eu sou a Lis, sua assistente virtual e estou aqui para facilitar o seu acesso às informações e serviços públicos de maneira mais rápida e eficiente.",
      time: time,
      type: "text",
    },
  ]);

  useEffect(() => {
    if (profile && !userInfoInjected.current) {
      userInfoInjected.current = true;
      const username =
        profile?.nomeSocial ?? profile?.social_name ?? profile?.name ?? "";
      setMessages(() => [
        {
          from: "lis",
          text: `Olá, ${username}, eu sou a Lis, sua assistente virtual e estou aqui para facilitar o seu acesso às informações e serviços públicos de maneira mais rápida e eficiente.`,
          time: time,
          type: "text",
        },
      ]);
    } else if (!profile && userInfoInjected.current) {
      userInfoInjected.current = false;
      conversationIdRef.current = null;
      setConversationId(null);
      localStorage.removeItem(CONVERSATION_STORAGE_KEY);
    }
  }, [profile, time]);

  const processUserMessage = useCallback(
    async (message: Message) => {
      cancelarVoz();

      if (message.from === MESSAGE_TYPES.USER) {
        setLoading(true);
      }

      let messageText = typeof message.text === "string" ? message.text : "";

      try {
        setMessages((prev) => [...prev, message]);

        if (profile) {
          userInfoInjected.current = true;
        } else if (!profile && userInfoInjected.current) {
          userInfoInjected.current = false;
          conversationIdRef.current = null;
          setConversationId(null);
          localStorage.removeItem(CONVERSATION_STORAGE_KEY);
        }

        if (!conversationIdRef.current && !validConversationIdRef.current) {
          const newConversationId = await getConversationId();
          conversationIdRef.current = newConversationId || null;
          setConversationId(newConversationId || null);
          validConversationIdRef.current = true;
        }

        const { text, messageId } = await getIAResposta(
          messageText,
          conversationIdRef.current,
          accessToken
        );

        if (message.type === MESSAGE_TYPES.AUDIO) {
          if (!messageId) {
            throw new Error("Missing X-Custom-Info message id");
          }
          const audioResponse = await getIAAudioReponse(messageId, text);
          const lisAudioMessage = createLisAudioMessage(audioResponse);
          return setMessages((prev) => [...prev, lisAudioMessage]);
        }

        const lisMessage = createLisMessage(text.toString());

        setMessages((prev) => [...prev, lisMessage]);
      } catch (error) {
        console.error("Erro ao obter resposta da IA:", error);
        const errorMessage = createErrorMessage();
        setMessages((prev) => [...prev, errorMessage]);
      } finally {
        setLoading(false);
      }
    },
    [profile, accessToken]
  );

  const props: CategoryProps = {
    messages,
    input,
    setInput,
    setMessages: processUserMessage,
    loading,
    avatarLis,
  };

  return (
    <AiAgentConversationProvider conversationId={conversationId}>
      <Chat {...props} />
    </AiAgentConversationProvider>
  );
};
